// ChatMessages.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  Avatar,
  useTheme,
  useMediaQuery,
  TextField,
  Button,
  MenuItem,
} from '@mui/material';
import StopIcon from '@mui/icons-material/Stop';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ChatIcon from '@mui/icons-material/Chat';
import { InlineErrorRecovery } from './ErrorRecovery';

// Add this mock at the very top of the file (for test environments)
if (typeof global !== 'undefined' && !global.ResizeObserver) {
  global.ResizeObserver = class {
    observe() {}

    unobserve() {}

    disconnect() {}
  };
}

// Simple typing effect hook
const useTypingEffect = (text, speed = 50, onComplete) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const intervalRef = useRef(null);
  const indexRef = useRef(0);

  const startTyping = () => {
    if (!text) return;

    setDisplayedText('');
    setIsTyping(true);
    indexRef.current = 0;

    intervalRef.current = setInterval(() => {
      if (indexRef.current < text.length) {
        setDisplayedText(text.slice(0, indexRef.current + 1));
        indexRef.current++;
      } else {
        setIsTyping(false);
        clearInterval(intervalRef.current);
        if (onComplete) onComplete();
      }
    }, speed);
  };

  const stopTyping = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    setIsTyping(false);
    setDisplayedText(text);
    if (onComplete) onComplete();
  };

  useEffect(() => () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  }, []);

  return {
    displayedText, isTyping, startTyping, stopTyping,
  };
};

// Helper function to format timestamps
const formatTime = (timestamp) => {
  if (!timestamp) return 'Now';

  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const hours = Math.floor(diffInMinutes / 60);
    if (hours < 24) return `${hours}h ago`;

    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  } catch (error) {
    return 'Now';
  }
};

function MessageBubble({
  message, darkMode, isMobile, onFormSubmit, isProcessing,
}) {
  const isUser = message.type === 'question';
  const isForm = message.type === 'form';
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Debug logging for message type
  console.log('🔍 MessageBubble received message:', {
    type: message.type,
    isForm,
    hasContent: !!message.content,
    hasFormFields: !!message.content?.formFields,
    formFieldsLength: message.content?.formFields?.length || 0,
  });

  // Enhanced text formatting and layout with better debugging
  const formatMessageContent = (content) => {
    if (!content || typeof content !== 'string') {
      return [{ type: 'text', content: 'No content available' }];
    }

    // console.log('🔍 Formatting message content:', content.substring(0, 100) + '...');

    // Handle code blocks with better regex
    const codeBlockRegex = /```(\w+)?\n?([\s\S]*?)```/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = codeBlockRegex.exec(content)) !== null) {
      // Add text before code block
      if (match.index > lastIndex) {
        const textBefore = content.slice(lastIndex, match.index).trim();
        if (textBefore) {
          parts.push({
            type: 'text',
            content: textBefore,
          });
        }
      }

      // Add code block
      parts.push({
        type: 'code',
        language: match[1] || 'text',
        content: match[2].trim(),
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < content.length) {
      const remainingText = content.slice(lastIndex).trim();
      if (remainingText) {
        parts.push({
          type: 'text',
          content: remainingText,
        });
      }
    }

    const result = parts.length > 0 ? parts : [{ type: 'text', content }];
    console.log('🔍 Formatted parts:', result.length, 'parts');
    return result;
  };

  // Enhanced text rendering with better formatting
  const renderFormattedText = (text) => {
    if (!text) return null;

    // Split by double newlines to create paragraphs
    const paragraphs = text.split(/\n\s*\n/);

    return paragraphs.map((paragraph, pIndex) => {
      if (!paragraph.trim()) return null;

      // Process inline formatting
      const lines = paragraph.split('\n');

      return (
        <Box key={pIndex} component="div" sx={{ mb: pIndex < paragraphs.length - 1 ? 1.5 : 0 }}>
          {lines.map((line, lIndex) => {
            if (!line.trim()) return <br key={lIndex} />;

            // Check if line is a header (# ## ### etc.)
            const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
            if (headerMatch) {
              const level = headerMatch[1].length;
              const headerText = headerMatch[2];
              const HeaderComponent = `h${Math.min(level + 2, 6)}`;

              return (
                <Typography
                  key={lIndex}
                  component={HeaderComponent}
                  variant={`h${Math.min(level + 3, 6)}`}
                  sx={{
                    fontWeight: 'bold',
                    mt: lIndex > 0 ? 1.5 : 0,
                    mb: 0.5,
                    color: 'inherit',
                    fontSize: {
                      xs: level <= 2 ? '1.1rem' : '1rem',
                      sm: level <= 2 ? '1.25rem' : '1.1rem',
                    },
                  }}
                >
                  {headerText}
                </Typography>
              );
            }

            // Check if line is a list item (- * + or numbered)
            const listMatch = line.match(/^(\s*)([-*+]|\d+\.)\s+(.+)$/);
            if (listMatch) {
              const indent = listMatch[1].length;
              const marker = listMatch[2];
              const listContent = listMatch[3];
              const isNumbered = /^\d+\.$/.test(marker);

              return (
                <Box
                  key={lIndex}
                  component="li"
                  sx={{
                    display: 'list-item',
                    listStyleType: isNumbered ? 'decimal' : 'disc',
                    ml: Math.max(2, indent / 2),
                    mb: 0.5,
                    '&::marker': {
                      color: 'inherit',
                    },
                  }}
                >
                  <Typography component="span" sx={{ color: 'inherit' }}>
                    {renderInlineFormatting(listContent)}
                  </Typography>
                </Box>
              );
            }

            // Regular text with inline formatting
            return (
              <Typography
                key={lIndex}
                component="div"
                sx={{
                  mb: lIndex < lines.length - 1 ? 0.5 : 0,
                  lineHeight: 1.6,
                  color: 'inherit',
                }}
              >
                {renderInlineFormatting(line)}
              </Typography>
            );
          })}
        </Box>
      );
    }).filter(Boolean);
  };

  // Enhanced inline formatting renderer
  const renderInlineFormatting = (text) => {
    if (!text) return '';

    // Handle bold (**text**), italic (*text*), and inline code (`code`)
    const parts = [];
    let currentIndex = 0;

    // Enhanced regex for better formatting detection
    const formatRegex = /(\*\*([^*]+?)\*\*|\*([^*\s][^*]*?[^*\s]|\w)\*|`([^`]+?)`)/g;
    let match;

    while ((match = formatRegex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > currentIndex) {
        parts.push(text.slice(currentIndex, match.index));
      }

      // Add formatted text
      if (match[2]) {
        // Bold text (**text**)
        parts.push(
          <Typography
            key={`bold-${match.index}`}
            component="strong"
            sx={{
              fontWeight: 700,
              color: 'inherit',
              fontSize: 'inherit',
            }}
          >
            {match[2]}
          </Typography>,
        );
      } else if (match[3]) {
        // Italic text (*text*)
        parts.push(
          <Typography
            key={`italic-${match.index}`}
            component="em"
            sx={{
              fontStyle: 'italic',
              color: 'inherit',
              fontSize: 'inherit',
            }}
          >
            {match[3]}
          </Typography>,
        );
      } else if (match[4]) {
        // Inline code (`code`)
        parts.push(
          <Typography
            key={`code-${match.index}`}
            component="code"
            sx={{
              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
              fontSize: '0.9em',
              backgroundColor: isUser
                ? 'rgba(255,255,255,0.25)'
                : (darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'),
              padding: '1px 4px',
              borderRadius: '3px',
              color: 'inherit',
              border: `1px solid ${isUser
                ? 'rgba(255,255,255,0.3)'
                : (darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)')}`,
            }}
          >
            {match[4]}
          </Typography>,
        );
      }

      currentIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (currentIndex < text.length) {
      parts.push(text.slice(currentIndex));
    }

    return parts.length > 1 ? parts : text;
  };

  const formattedContent = formatMessageContent(message.content);

  // CRITICAL FIX: Render form messages
  console.log('🔍 Form rendering check:', {
    isForm,
    hasContent: !!message.content,
    hasFormFields: !!message.content?.formFields,
    condition: isForm && message.content && message.content.formFields,
  });

  if (isForm && message.content && message.content.formFields) {
    console.log('✅ RENDERING FORM MESSAGE!');
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          mb: 2,
        }}
      >
        <Paper
          elevation={1}
          sx={{
            maxWidth: '85%',
            minWidth: '300px',
            p: 2,
            backgroundColor: darkMode ? '#2d2d2d' : '#f5f5f5',
            border: `1px solid ${darkMode ? '#444' : '#e0e0e0'}`,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" gutterBottom>
            {message.content.title || 'Form'}
          </Typography>
          {message.content.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {message.content.description}
            </Typography>
          )}

          {/* Render form fields */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {message.content.formFields.map((field, index) => {
              // Enhanced debug logging for field data
              console.log(`🔍 Rendering field ${index}:`, field);
              console.log(`🔍 Field type check: "${field.type}" === "select"?`, field.type === 'select');
              console.log('🔍 Field type typeof:', typeof field.type);
              console.log('🔍 Field type JSON:', JSON.stringify(field.type));

              if (field.type === 'select') {
                console.log('🔍 Select field options:', field.options);
              }

              return (
                <Box key={field.name || index}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                    {field.label}
                    {' '}
                    (Type:
                    {field.type}
                    )
                    {field.required && <span style={{ color: 'red' }}> *</span>}
                  </Typography>
                  {field.type === 'text' && (
                    <TextField
                      fullWidth
                      placeholder={field.placeholder || `Enter ${field.label}`}
                      size="small"
                      variant="outlined"
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, [field.name]: e.target.value }))}
                    />
                  )}
                  {field.type === 'date' && (
                    <TextField
                      fullWidth
                      type="date"
                      size="small"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, [field.name]: e.target.value }))}
                    />
                  )}
                  {field.type === 'number' && (
                    <TextField
                      fullWidth
                      type="number"
                      placeholder={field.placeholder || `Enter ${field.label}`}
                      size="small"
                      variant="outlined"
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, [field.name]: e.target.value }))}
                    />
                  )}
                  {field.type === 'select' && (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '12px', color: 'blue', display: 'block', mb: 1,
                        }}
                      >
                        DEBUG: Rendering select field with
                        {' '}
                        {field.options?.length || 0}
                        {' '}
                        options
                      </Typography>
                      {/* Try both Material-UI Select and native HTML select */}
                      <TextField
                        fullWidth
                        select
                        size="small"
                        variant="outlined"
                        value={formData[field.name] || ''}
                        label={field.label}
                        onChange={(e) => setFormData((prev) => ({ ...prev, [field.name]: e.target.value }))}
                        SelectProps={{
                          displayEmpty: true,
                          native: false, // Use Material-UI select
                        }}
                      >
                        <MenuItem value="" disabled>
                          <em>
                            Select
                            {field.label}
                          </em>
                        </MenuItem>
                        {field.options && field.options.map((option) => {
                          console.log('🔍 Rendering option:', option);
                          return (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          );
                        })}
                      </TextField>

                      {/* Fallback: Native HTML select for debugging */}
                      <Box
                        component="select"
                        sx={{
                          marginTop: '10px', padding: '8px', width: '100%', border: '1px solid #ccc',
                        }}
                      >
                        <option value="">
                          Select
                          {field.label}
                          {' '}
                          (HTML fallback)
                        </option>
                        {field.options && field.options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Box>
                    </Box>
                  )}
                  {field.type === 'address' && (
                    <TextField
                      fullWidth
                      placeholder="Enter address"
                      size="small"
                      variant="outlined"
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, [field.name]: e.target.value }))}
                    />
                  )}

                  {/* Catch-all for debugging unknown field types */}
                  {!['text', 'date', 'number', 'select', 'address'].includes(field.type) && (
                    <Box sx={{ padding: '10px', border: '2px solid red', backgroundColor: '#ffe6e6' }}>
                      <Typography variant="body2" component="strong">
                        UNKNOWN FIELD TYPE: "
                        {field.type}
                        "
                      </Typography>
                      <Box component="pre" sx={{ fontSize: '12px' }}>{JSON.stringify(field, null, 2)}</Box>
                    </Box>
                  )}
                </Box>
              );
            })}

            {/* Submit button */}
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 2, alignSelf: 'flex-start' }}
              disabled={isSubmitting || isProcessing}
              onClick={async () => {
                console.log('📝 Form submission started:', { formId: message.content.formId, formData });
                setIsSubmitting(true);

                try {
                  if (onFormSubmit) {
                    await onFormSubmit(message.content.formId, formData);
                  }
                } catch (error) {
                  console.error('Form submission error:', error);
                } finally {
                  setIsSubmitting(false);
                }
              }}
              startIcon={isSubmitting || isProcessing ? <CircularProgress size={16} /> : null}
            >
              {isSubmitting ? 'Submitting...' : isProcessing ? 'AI is thinking...' : 'Submit'}
            </Button>
          </Box>

          {/* Timestamp */}
          <Typography
            variant="caption"
            sx={{
              opacity: 0.7,
              fontSize: '0.75rem',
              mt: 1,
              display: 'block',
            }}
          >
            {formatTime(message.timestamp)}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: { xs: 1.5, sm: 2 },
        px: { xs: 0, sm: 0 },
      }}
    >
      <Paper
        elevation={isUser ? 2 : 1}
        sx={{
          p: { xs: 2, sm: 2.5 },
          backgroundColor: isUser
            ? (darkMode ? '#1976d2' : '#2196f3')
            : (darkMode ? 'grey.800' : 'grey.50'),
          color: isUser
            ? '#fff'
            : (darkMode ? 'grey.100' : 'grey.900'),
          borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
          maxWidth: { xs: '90%', sm: '75%', md: '70%' },
          wordBreak: 'break-word',
          '& > *:first-of-type': {
            marginTop: 0,
          },
          '& > *:last-child': {
            marginBottom: 0,
          },
          '& ul, & ol': {
            margin: '8px 0',
            paddingLeft: '20px',
          },
          '& li': {
            marginBottom: '4px',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* Enhanced message content rendering */}
          <Box sx={{ mb: 1 }}>
            {formattedContent.map((part, index) => {
              if (part.type === 'code') {
                return (
                  <Box
                    key={index}
                    sx={{
                      backgroundColor: isUser
                        ? 'rgba(255,255,255,0.15)'
                        : (darkMode ? '#1a1a1a' : '#f5f5f5'),
                      border: `1px solid ${isUser
                        ? 'rgba(255,255,255,0.25)'
                        : (darkMode ? '#333' : '#e0e0e0')}`,
                      borderRadius: 1,
                      p: 1.5,
                      my: 1.5,
                      overflow: 'auto',
                      position: 'relative',
                    }}
                  >
                    {part.language && part.language !== 'text' && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mb: 1,
                          opacity: 0.8,
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          color: 'inherit',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        {part.language}
                      </Typography>
                    )}
                    <Typography
                      component="pre"
                      sx={{
                        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                        fontSize: '0.85rem',
                        lineHeight: 1.4,
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        color: 'inherit',
                        overflow: 'auto',
                      }}
                    >
                      {part.content}
                    </Typography>
                  </Box>
                );
              }
              return (
                <Box key={index} sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  {renderFormattedText(part.content)}
                </Box>
              );
            })}
          </Box>

          {/* Enhanced timestamp styling */}
          {/* Error Recovery Component */}
          {message.metadata?.error && message.metadata?.showErrorRecovery && (
            <InlineErrorRecovery
              error={message.content}
              executionId={message.metadata?.executionId}
              onRetry={() => {
                // Handle retry - could be passed as prop or use a callback
                console.log('Retry requested for execution:', message.metadata?.executionId);
              }}
              onRecoverySuccess={(result) => {
                console.log('Recovery successful:', result);
              }}
              onShowDetails={() => {
                // Show detailed error recovery modal
                console.log('Show error details for:', message.metadata?.executionId);
              }}
              compact={isMobile}
            />
          )}

          <Typography
            variant="caption"
            sx={{
              opacity: 0.7,
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
              textAlign: isUser ? 'right' : 'left',
              mt: 0.5,
              lineHeight: 1,
              color: 'inherit',
            }}
          >
            {formatTime(message.timestamp)}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}

export default function ChatMessages({
  messages, darkMode, isLoading, error, onRetry, onFormSubmit, isProcessing,
}) {
  let theme; let
    isMobile;
  try {
    theme = useTheme();
    isMobile = useMediaQuery ? useMediaQuery(theme.breakpoints.down('sm')) : false;
  } catch (e) {
    theme = { breakpoints: { down: () => false } };
    isMobile = false;
  }

  const containerRef = useRef(null);

  // Scroll to bottom effect
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        overflowY: 'auto',
        p: { xs: 2, sm: 3 },
        pb: 8,
        position: 'relative',
        // Smooth scrolling effect
        '&::-webkit-scrollbar': {
          width: '8px',
          backgroundColor: darkMode ? '#121212' : '#f5f5f5',
        },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: '4px',
          backgroundColor: darkMode ? '#333' : '#ccc',
        },
      }}
    >
      {/* Loader and error handling */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
          <IconButton onClick={onRetry} size="small" sx={{ ml: 1 }}>
            <SmartToyIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      {/* Welcome message when no messages */}
      {messages.length === 0 && !isLoading && !error && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100%',
          py: 8,
          px: 3,
          textAlign: 'center',
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
        }}
        >
          {/* Hero Section */}
          <Box sx={{ mb: 8, maxWidth: 800 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                lineHeight: 1.2,
                background: 'linear-gradient(135deg, #ffffff 0%, #e3f2fd 30%, #bbdefb 70%, #90caf9 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 4px 8px rgba(0,0,0,0.1)',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
              }}
            >
              Delight customers. Empower your team.
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 600,
                mb: 4,
                fontSize: { xs: '1.8rem', md: '2.2rem' },
                background: 'linear-gradient(135deg, #64b5f6 0%, #90caf9 50%, #bbdefb 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))',
              }}
            >
              AI Agents for everyone.
            </Typography>
            <Typography
              variant="h6"
              sx={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0.7) 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 6,
                maxWidth: 600,
                mx: 'auto',
                fontSize: '1.2rem',
                lineHeight: 1.6,
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              Experience the power of MCP integration with your agentic flows as tools.
              Build intelligent workflows that connect seamlessly with external services.
            </Typography>

            {/* CTA Buttons */}
            <Box sx={{
              display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap',
            }}
            >
              <Button
                variant="contained"
                size="large"
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 3,
                  textTransform: 'none',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.9)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Start Building
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 3,
                  textTransform: 'none',
                  borderWidth: 2,
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Get a Demo
              </Button>
            </Box>
          </Box>

          {/* Trust Indicators */}
          <Box sx={{ mb: 8, opacity: 0.7 }}>
            <Typography variant="body2" sx={{ mb: 3, color: 'rgba(255,255,255,0.6)' }}>
              Trusted by leading companies
            </Typography>
            <Box sx={{
              display: 'flex',
              gap: 4,
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
            >
              {['Anthropic', 'OpenAI', 'LangChain', 'MongoDB', 'React'].map((company) => (
                <Typography
                  key={company}
                  variant="h6"
                  sx={{
                    fontWeight: 500,
                    color: 'rgba(255,255,255,0.8)',
                    fontSize: '1rem',
                  }}
                >
                  {company}
                </Typography>
              ))}
            </Box>
          </Box>

          {/* Features Grid */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 4,
            maxWidth: 1000,
            width: '100%',
            mb: 6,
          }}
          >
            {/* Agentic Flows as Tools */}
            <Paper sx={{
              p: 4,
              textAlign: 'center',
              bgcolor: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 3,
              '&:hover': {
                transform: 'translateY(-8px)',
                bgcolor: 'rgba(255,255,255,0.15)',
                boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
              },
              transition: 'all 0.3s ease',
            }}
            >
              <Box sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
              >
                <ChatIcon sx={{ fontSize: 30, color: 'white' }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'white' }}>
                Agentic Flows as Tools
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.6 }}>
                Your custom agentic flows are now available as MCP tools. Execute complex workflows directly in chat conversations.
              </Typography>
            </Paper>

            {/* MCP Server Integration */}
            <Paper sx={{
              p: 4,
              textAlign: 'center',
              bgcolor: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 3,
              '&:hover': {
                transform: 'translateY(-8px)',
                bgcolor: 'rgba(255,255,255,0.15)',
                boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
              },
              transition: 'all 0.3s ease',
            }}
            >
              <Box sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
              >
                <SmartToyIcon sx={{ fontSize: 30, color: 'white' }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'white' }}>
                MCP Server Integration
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.6 }}>
                Connect to external MCP servers and access their tools seamlessly in your conversations with enterprise-grade security.
              </Typography>
            </Paper>

            {/* Rich Form Support */}
            <Paper sx={{
              p: 4,
              textAlign: 'center',
              bgcolor: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 3,
              '&:hover': {
                transform: 'translateY(-8px)',
                bgcolor: 'rgba(255,255,255,0.15)',
                boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
              },
              transition: 'all 0.3s ease',
            }}
            >
              <Box sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
              >
                <PersonIcon sx={{ fontSize: 30, color: 'white' }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'white' }}>
                Rich Form Support
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.6 }}>
                Interactive forms with dynamic fields, validation, and real-time processing capabilities for seamless user experiences.
              </Typography>
            </Paper>
          </Box>

          {/* Bottom CTA */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255,255,255,0.7)',
                mb: 3,
                fontSize: '1.1rem',
              }}
            >
              Ready to get started? Type a message below or select an agentic flow from the options drawer.
            </Typography>
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1,
              color: 'rgba(255,255,255,0.6)',
            }}
            >
              <Typography variant="body2">↓</Typography>
              <Typography variant="body2">Start typing to begin</Typography>
              <Typography variant="body2">↓</Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Message rendering */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {messages.map((message, index) => (
          <MessageBubble
            key={index}
            message={message}
            darkMode={darkMode}
            isMobile={isMobile}
            onFormSubmit={onFormSubmit}
            isProcessing={isProcessing}
          />
        ))}
      </Box>
    </Box>
  );
}
